import { useRef, useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';

import type { EditableColumnEditor } from '../types';
import { CellValidationTooltip } from './cell-validation-tooltip';

type EditableSelectInputProps = {
  label: string;
  value: string;
  placeholder?: string;
  className?: string;
  options: Extract<EditableColumnEditor, { type: 'select' }>['options'];
  renderTrigger?: Extract<EditableColumnEditor, { type: 'select' }>['renderTrigger'];
  validationMessage: string | null;
  onCancel: () => void;
  onChange: (value: string) => void;
};

export function EditableSelectInput({
  label,
  value,
  placeholder,
  className,
  options,
  renderTrigger,
  validationMessage,
  onCancel,
  onChange,
}: EditableSelectInputProps) {
  const didSelectValueRef = useRef(false);
  const [open, setOpen] = useState(true);

  return (
    <Select
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);

        if (!nextOpen) {
          queueMicrotask(() => {
            if (didSelectValueRef.current) {
              // Ignore the close event caused by a selection so invalid picks can keep the cell active.
              didSelectValueRef.current = false;
              return;
            }

            onCancel();
          });
        }
      }}
      value={value || undefined}
      onValueChange={(nextValue) => {
        didSelectValueRef.current = true;
        onChange(nextValue);
      }}
    >
      <CellValidationTooltip message={validationMessage}>
        <SelectTrigger
          size="sm"
          aria-label={label}
          aria-invalid={!!validationMessage || undefined}
          className={className}
        >
          <SelectValue placeholder={placeholder}>
            {!value ? undefined : renderTrigger?.(value)}
          </SelectValue>
        </SelectTrigger>
      </CellValidationTooltip>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} textValue={option.textLabel}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
